import React, { useState, useEffect } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "../../css/ReviewForm.css";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import RateReviewIcon from "@mui/icons-material/RateReview";
import StarIcon from "@mui/icons-material/Star";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";

function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterRating, setFilterRating] = useState("all");
    const apiUrl = `${process.env.REACT_APP_API}`;
    const [auth] = useAuth();
    const navigate = useNavigate();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${apiUrl}/reviews`);
                const data = await response.json();
                // Sort reviews by date (newest first)
                const sortedReviews = data.data.sort((a, b) =>
                    new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                );
                setReviews(sortedReviews);
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setIsLoading(false);
                toast.error("Error loading reviews");
            }
        };
        fetchReviews();
    }, []);

    const renderStars = (rating) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <FontAwesomeIcon
                    icon={faStar}
                    key={i}
                    style={{
                        color: i < rating ? '#FECB38' : '#e0e0e0',
                        fontSize: '1.2rem'
                    }}
                />
            );
        }
        return stars;
    };

    const getFilteredReviews = () => {
        if (filterRating === "all") {
            return reviews;
        }
        return reviews.filter(review => review.rating === parseInt(filterRating));
    };

    const filteredReviews = getFilteredReviews();

    const getAverageRating = () => {
        if (reviews.length === 0) return 0;
        const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
        return (sum / reviews.length).toFixed(1);
    };

    const getRatingCounts = () => {
        const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviews.forEach(review => {
            counts[review.rating]++;
        });
        return counts;
    };

    const ratingCounts = getRatingCounts();
    const averageRating = getAverageRating();

    return (
        <>
            <Header />
            <div className="reviews-page" style={{ backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
                <div className="container py-5">
                    {/* Header Section */}
                    <div className="text-center mb-5">
                        <div className="reviews-icon-wrapper mb-3">
                            <RateReviewIcon style={{ fontSize: "3.5rem", color: "white" }} />
                        </div>
                        <h1 style={{ color: "#002e6a", fontWeight: "700" }}>
                            Customer Reviews
                        </h1>
                        <p className="text-muted">See what our customers are saying</p>
                    </div>

                    {/* Summary Section */}
                    {!isLoading && reviews.length > 0 && (
                        <div className="row mb-4">
                            <div className="col-lg-8 mx-auto">
                                <div className="card shadow-sm border-0 p-4">
                                    <div className="row align-items-center">
                                        <div className="col-md-4 text-center border-end">
                                            <h1 className="mb-1" style={{ fontSize: "3.5rem", fontWeight: "700", color: "#002e6a" }}>
                                                {averageRating}
                                            </h1>
                                            <div className="mb-2">
                                                {renderStars(Math.round(averageRating))}
                                            </div>
                                            <p className="text-muted mb-0">
                                                Based on {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                                            </p>
                                        </div>
                                        <div className="col-md-5 mt-3 mt-md-0">
                                            {[5, 4, 3, 2, 1].map(rating => (
                                                <div key={rating} className="d-flex align-items-center mb-2">
                          <span className="me-2" style={{ minWidth: "60px" }}>
                            {rating} <FontAwesomeIcon icon={faStar} style={{ color: '#FECB38', fontSize: '0.9rem' }} />
                          </span>
                                                    <div className="progress flex-grow-1" style={{ height: "10px" }}>
                                                        <div
                                                            className="progress-bar"
                                                            style={{
                                                                width: `${reviews.length > 0 ? (ratingCounts[rating] / reviews.length * 100) : 0}%`,
                                                                backgroundColor: "#002e6a"
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="ms-2 text-muted" style={{ minWidth: "40px" }}>
                            ({ratingCounts[rating]})
                          </span>
                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filter Section */}
                    {!isLoading && reviews.length > 0 && (
                        <div className="row mb-4">
                            <div className="col-lg-8 mx-auto">
                                <div className="d-flex justify-content-center gap-2 flex-wrap">
                                    <button
                                        className={`btn filter-button ${filterRating === "all" ? "active" : ""}`}
                                        onClick={() => setFilterRating("all")}
                                    >
                                        All ({reviews.length})
                                    </button>
                                    {[5, 4, 3, 2, 1].map(rating => (
                                        ratingCounts[rating] > 0 && (
                                            <button
                                                key={rating}
                                                className={`btn filter-button ${filterRating === rating.toString() ? "active" : ""}`}
                                                onClick={() => setFilterRating(rating.toString())}
                                            >
                                                {rating} <FontAwesomeIcon icon={faStar} style={{ color: '#FECB38', fontSize: '0.9rem' }} /> ({ratingCounts[rating]})
                                            </button>
                                        )
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div className="text-center my-5">
                            <div className="spinner-border" style={{ color: "#c70044" }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading reviews...</p>
                        </div>
                    )}

                    {/* Reviews List */}
                    {!isLoading && filteredReviews.length > 0 && (
                        <div className="row">
                            <div className="col-lg-8 mx-auto">
                                <div className="mb-3">
                                    <small className="text-muted">
                                        Showing {filteredReviews.length} {filteredReviews.length === 1 ? 'review' : 'reviews'}
                                    </small>
                                </div>
                                {filteredReviews.map((review, index) => (
                                    <div key={index} className="card review-card shadow-sm border-0 mb-3">
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-circle me-3">
                                                        <PersonIcon style={{ fontSize: "1.5rem", color: "white" }} />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0" style={{ fontWeight: "600", color: "#002e6a" }}>
                                                            {review.name}
                                                        </h6>
                                                        {review.createdAt && (
                                                            <small className="text-muted">
                                                                {new Date(review.createdAt).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="rating-stars">
                                                    {renderStars(review.rating)}
                                                </div>
                                            </div>
                                            <p className="review-text mb-0" style={{ color: "#555", lineHeight: "1.6" }}>
                                                "{review.comment}"
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No Filtered Results */}
                    {!isLoading && reviews.length > 0 && filteredReviews.length === 0 && (
                        <div className="text-center my-5">
                            <p className="text-muted">No reviews match this filter</p>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => setFilterRating("all")}
                            >
                                Show All Reviews
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <Footer />

            <style jsx>{`
        .reviews-icon-wrapper {
          width: 100px;
          height: 100px;
          background-color: #002e6a;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px rgba(0, 46, 106, 0.3);
        }

        .review-card {
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .review-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
        }

        .avatar-circle {
          width: 50px;
          height: 50px;
          background-color: #c70044;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .rating-stars {
          display: flex;
          gap: 4px;
        }

        .review-text {
          font-size: 1rem;
          font-style: italic;
        }

        .write-review-button {
          background-color: #c70044;
          color: white;
          border: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .write-review-button:hover {
          background-color: #a00036;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.3);
        }

        .filter-button {
          border: 2px solid #e0e0e0;
          background-color: white;
          color: #555;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .filter-button:hover {
          border-color: #002e6a;
          color: #002e6a;
        }

        .filter-button.active {
          background-color: #002e6a;
          border-color: #002e6a;
          color: white;
        }

        .progress {
          border-radius: 10px;
          background-color: #e0e0e0;
        }

        .progress-bar {
          border-radius: 10px;
        }

        @media (max-width: 768px) {
          .reviews-icon-wrapper {
            width: 80px;
            height: 80px;
          }

          .avatar-circle {
            width: 40px;
            height: 40px;
          }

          .border-end {
            border-right: none !important;
            border-bottom: 1px solid #dee2e6;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
          }
        }
      `}</style>
        </>
    );
}

export default Reviews;